import React, { useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { withStyles, Typography, Grid } from '@material-ui/core';
import { Search as SearchIcon, Close as CloseIcon } from '@material-ui/icons';
import { getExpediteDailyParts, setExpediteDailyPartsFilters } from '~/store/expedite/actions';
import PF2MClearFiltersButton from '~/components/PF2MClearFiltersButton';
import PF2MSearchButton from '~/components/PF2MSearchButton';
import { newMomentDate, timestampToDate } from '~/utils/moment';
import {
  Card, CardHeader, CardIcon, CardBody,
} from '~/components/Card';
import { DateField, SelectField, CheckboxField } from './FormFields';
import styles from './styles';

const SearchForm = ({ classes, onClose, onSearch }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const materials = useSelector(state => state.manager.materials);
  const expediteMaterials = useSelector(state => state.expedite.expediteMaterials);
  const subElements = useSelector(state => state.manager.subElements);
  const filters = useSelector(state => state.expedite.expediteDailyPartsFilters);
  const pagination = useSelector(state => state.expedite.pagination?.expediteDailyPartsPagination);

  const externalProductList = useMemo(() => {
    const materialMap = new Map(
      materials.map(material => [material.id, material.name]),
    );

    return expediteMaterials.map(exMaterial => ({
      ...exMaterial,
      name: materialMap.get(exMaterial.material_id) ?? null,
    }));
  }, [materials, expediteMaterials]);

  const formattedMenuItems = (options, key = 'id', showInactive = false) => {
    if (!options || options.length === 0) return [];

    const orderedOptions = options.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, {
      sensitivity: 'base',
      numeric: true,
      ignorePunctuation: true,
    }));

    const formattedOptions = orderedOptions.map(item => ({
      label: `${item?.name} ${showInactive && !item?.active ? `- ${translate('common:Inactive')}` : ''}`,
      value: item[key],
    }));
    return formattedOptions;
  };

  const addDefaultOption = options => [{ value: 0, label: translate('common:Select') }, ...options || []];

  const expediteMaterialsOptions = useMemo(() => {
    const formatted = formattedMenuItems(
      externalProductList,
      'external_product_id',
    );
    return addDefaultOption(formatted);
  }, [externalProductList, translate]);

  const subElementsOptions = useMemo(() => {
    const formatted = formattedMenuItems(subElements, 'id', true);
    return addDefaultOption(formatted);
  }, [subElements, translate]);

  const fields = values => ([
    {
      key: 'startDate',
      label: translate('common:InitialDate'),
      component: <DateField name="startDate" />,
    },
    {
      key: 'endDate',
      label: translate('common:FinalDate'),
      component: <DateField name="endDate" />,
    },
    {
      key: 'external_product_id',
      label: translate('common:Material'),
      component: <SelectField name="external_product_id" options={expediteMaterialsOptions} />,
    },
    {
      key: 'element_point_id',
      label: translate('common:OriginPoint'),
      component: <SelectField name="element_point_id" options={subElementsOptions} />,
    },
    {
      key: 'hide_deleted',
      component: <CheckboxField name="hide_deleted" label={translate('common:HideDeletedOrders')} checked={values?.hide_deleted ?? false} />,
    },
  ]);


  const validate = (values) => {
    const errors = {};
    const {
      startDate,
      endDate,
    } = values;
    const startDateMoment = moment(startDate);
    const endDateMoment = moment(endDate);

    if (!startDateMoment.isValid()) {
      errors.startDate = translate('validation:InitialDateRequired');
    }

    if (!endDateMoment.isValid()) {
      errors.endDate = translate('validation:FinalDateRequired');
    }

    if (startDateMoment.isValid() && endDateMoment.isValid()) {
      if (startDateMoment.isAfter(endDateMoment)) {
        errors.endDate = translate('validation:FinalDateSmallerThanInitialDate');
      }

      const days = endDateMoment.diff(startDateMoment, 'days');
      if (days < 0) {
        errors.endDate = translate('validation:FinalDateSmallerThanInitialDate');
      }

      const searchLimit = 7;
      if (days > searchLimit) {
        errors.endDate = `${translate('validation:SearchTimeRangeCantExceed')} ${searchLimit} ${translate('common:Days').toLowerCase()}`;
      }
    }
    return errors;
  };

  const handleSubmit = (values) => {
    const {
      startDate,
      endDate,
    } = values;

    const startTimestamp = moment(startDate).startOf('day').unix();
    const endTimestamp = moment(endDate).endOf('day').unix();

    const newFilters = {
      ...values,
      start_timestamp: startTimestamp,
      end_timestamp: endTimestamp,
    };
    dispatch(setExpediteDailyPartsFilters(newFilters));
    dispatch(getExpediteDailyParts({
      parameters: newFilters,
      page: 0,
      limit: pagination?.batchSize || 1500,
      nextBatchPage: pagination?.nextBatchPage || 0,
      pageSize: pagination?.pageSize || 10,
    }));
    onSearch();
  };

  const initialValues = {
    startDate: filters.start_timestamp
      ? timestampToDate(filters.start_timestamp)
      : newMomentDate(),
    endDate: filters.end_timestamp
      ? timestampToDate(filters.end_timestamp)
      : newMomentDate(),
    ...filters,
  };

  const disabledSearch = (isSubmitting, values) => {
    if (isSubmitting) return true;

    const { startDate, endDate } = values || {};
    if (!startDate || !endDate) return true;

    const momentStartDate = moment(startDate);
    const momentEndDate = moment(endDate);

    if (!momentStartDate.isValid() || !momentEndDate.isValid()) return true;

    if (momentStartDate.isAfter(momentEndDate)) return true;

    return momentEndDate.diff(momentStartDate, 'days') > 7;
  };

  const handleResetForm = (resetForm) => {
    resetForm({
      startDate: newMomentDate(),
      endDate: newMomentDate(),
      hide_deleted: false,
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        values,
        resetForm,
      }) => (
        <Form>
          <Card className={classes.card}>
            <CardHeader icon>
              <>
                <CardIcon color="warning">
                  <SearchIcon />
                </CardIcon>
                <CardIcon
                  onClick={onClose}
                  className={classes.closeIcon}
                >
                  <CloseIcon />
                </CardIcon>
              </>
            </CardHeader>
            <CardBody>
              <Typography variant="h4" align="center" gutterBottom>
                {translate('common:Search').toUpperCase()}
              </Typography>
              <Grid container spacing={2}>
                {fields(values).map(item => (
                  <Grid item key={item.key} xs={12} sm={6} className={classes.fieldsContainer}>
                    <span className={classes.labelSpan}>{item.label}</span>
                    {item.component}
                  </Grid>
                ))}
              </Grid>
              <Grid item className={classes.actionsButtonsContainer}>
                <PF2MClearFiltersButton
                  onClick={() => handleResetForm(resetForm)}
                />
                <PF2MSearchButton
                  type="submit"
                  disabled={disabledSearch(isSubmitting, values)}
                  onClick={null}
                />
              </Grid>
            </CardBody>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

SearchForm.propTypes = {
  classes: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSearch: PropTypes.func,
};

SearchForm.defaultProps = {
  onSearch: () => {},
  onClose: () => {},
};

export default withStyles(styles)(SearchForm);
